import React, { useState, useEffect, useContext } from 'react';
import { ArrowUp, ArrowDown, TrendingUp, TrendingDown, Activity, PieChart, Briefcase, Layers, Info, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StockService from '../../Api_Module/Api_Services/StockService';
import { formatNumber } from '../../utils/Numberformat';
import { useTheme } from '../ThemeContext';
import { UserContext } from '../../context/UserContext';
import LockPill from '../common/LockPill';

interface TopStock {
    symbol: string;
    name?: string;
    value?: number;
    change?: number;
    count?: number;
    price?: number;
    imageUrl?: string;
}

interface DashboardSectionProps {
    title: string;
    icon: React.ReactNode;
    data: TopStock[];
    loading: boolean;
    type: 'value' | 'count' | 'change' | 'number';
    color: string;
    topN: number;
}

const DashboardCard: React.FC<DashboardSectionProps> = ({ title, icon, data, loading, type, color, topN }) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { userData } = useContext(UserContext) || {};
    const hasValidAccess = Boolean(
        userData && (
            (userData.subscriptionStatus === 'active' && userData.planActive === true) ||
            (userData.subscriptionStatus === 'trialing' && userData.planActive === true && (userData as any).isTrial === true) ||
            ((userData as any).isTrial === true && ((userData as any).daysLeft ?? 0) > 0)
        )
    );

    const cardBg = theme === 'dark' ? 'bg-theme-secondary' : 'bg-white';
    const cardBorder = theme === 'dark' ? 'border-theme-tertiary' : 'border-gray-200';
    const textPrimary = theme === 'dark' ? 'text-theme-primary' : 'text-gray-900';
    const textSecondary = theme === 'dark' ? 'text-theme-muted' : 'text-gray-600';

    const handleStockClick = (symbol: string) => {
        if (hasValidAccess) {
            navigate(`/${symbol}/ownership/institutions`);
        } else {
            // navigate('/login'); // Optional: redirect to login if clicked
        }
    };

    return (
        <div className={`border ${cardBorder} ${cardBg} rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full overflow-hidden`}>
            <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
                    {React.cloneElement(icon as React.ReactElement, { className: `w-5 h-5 ${color.replace('bg-', 'text-')}` })}
                </div>
                <h3 className={`font-bold text-base sm:text-lg ${textPrimary}`}>{title}</h3>
            </div>

            <div className="flex-1">
                {loading ? (
                    <div className="space-y-3 animate-pulse">
                        {Array.from({ length: Math.min(topN, 5) }).map((_, i) => (
                            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-3 relative">
                        {data.slice(0, topN).map((item, idx) => {
                            const locked = !hasValidAccess;
                            return (
                                <div
                                    key={item.symbol}
                                    onClick={() => { if (!locked) handleStockClick(item.symbol); }}
                                    className={`flex items-center justify-between p-2 rounded-lg ${locked ? 'cursor-default' : 'cursor-pointer'} transition-colors ${!locked ? (theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-100') : ''}`}
                                >
                                    <div className="flex items-center gap-3">
                                        {locked ? (
                                            <LockPill size="sm" />
                                        ) : (
                                            <>
                                                <span className={`w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full ${idx < 3 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                                                    {idx + 1}
                                                </span>
                                                {(
                                                    <img
                                                        src={item.imageUrl || `https://images.financialmodelingprep.com/symbol/${item.symbol}.png`}
                                                        alt={item.symbol}
                                                        className="w-6 h-6 rounded-sm border border-gray-200 dark:border-gray-700 object-contain bg-white dark:bg-gray-900"
                                                    />
                                                )}
                                                <div>
                                                    <div className={`font-semibold text-sm sm:text-base ${textPrimary}`}>{item.symbol}</div>
                                                    {item.name && <div className={`text-xs ${textSecondary} truncate max-w-[100px] sm:max-w-[120px]`}>{item.name}</div>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        {type === 'value' && (
                                            <div className={`font-bold text-sm sm:text-base ${textPrimary}`}>${formatNumber(item.value || 0)}</div>
                                        )}
                                        {type === 'count' && (
                                            <div className={`font-bold text-sm sm:text-base ${textPrimary}`}>{item.count?.toLocaleString()} Funds</div>
                                        )}
                                        {type === 'number' && (
                                            <div className={`font-bold text-sm sm:text-base ${textPrimary}`}>{formatNumber(item.value || 0)}</div>
                                        )}
                                        {type === 'change' && (
                                            <div className={`font-bold ${item.change && item.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                {item.change && item.change > 0 ? '+' : ''}{item.change}%
                                            </div>
                                        )}
                                        {item.change !== undefined && item.change !== null && Math.abs(item.change) >= 0.01 && type !== 'change' && (
                                            <div className={`text-xs ${item.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change).toFixed(2)}%
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {data.length === 0 && (
                            <div className={`text-center py-8 ${textSecondary} text-sm`}>No data available</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const InstitutionalActivityDashboard: React.FC = () => {
    const { theme } = useTheme();

    // Dynamic Top N filter
    const [topN, setTopN] = useState<number>(5);

    // Individual loading states for async loading
    const [loadingStates, setLoadingStates] = useState({
        calls: true,
        puts: true,
        newPositions: true,
        increased: true,
        reduced: true,
        fundsHolding: true,
        closed: true,
    });

    const [data, setData] = useState<{
        reduced: TopStock[];
        puts: TopStock[];
        newPositions: TopStock[];
        increased: TopStock[];
        fundsHolding: TopStock[];
        closed: TopStock[];
        calls: TopStock[];
    }>({
        reduced: [],
        puts: [],
        newPositions: [],
        increased: [],
        fundsHolding: [],
        closed: [],
        calls: [],
    });

    const [totals, setTotals] = useState({
        calls: 0,
        puts: 0,
        newPositions: 0
    });

    // Helper to extract array from response
    const getArr = (res: any) => {
        if (!res) return [];
        if (Array.isArray(res)) return res;
        if (Array.isArray(res.data)) return res.data;
        if (Array.isArray(res.items)) return res.items;
        return [];
    };

    // Recalculate totals whenever data or topN changes
    useEffect(() => {
        setTotals({
            calls: data.calls.slice(0, topN).reduce((acc, item) => acc + (item.value || 0), 0),
            puts: data.puts.slice(0, topN).reduce((acc, item) => acc + (item.value || 0), 0),
            newPositions: data.newPositions.slice(0, topN).reduce((acc, item) => acc + (item.count || 0), 0)
        });
    }, [data.calls, data.puts, data.newPositions, topN]);

    useEffect(() => {
        // Mapping functions based on API response structure
        const mapReduced = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            count: item.reducedPositions,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapPuts = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            value: item.totalPuts,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapNewPos = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            count: item.newPositions,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapIncreased = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            count: item.increasedPositions,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapFunds = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            count: item.fundsHolding,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapClosed = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            count: item.closedPositions,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        const mapCalls = (items: any[]): TopStock[] => items.map(item => ({
            symbol: item.symbol,
            name: item.companyName,
            value: item.totalCalls,
            change: item.returnSinceDate,
            imageUrl: item.imageUrl
        }));

        // Fetch all data when component mounts (not at module level)
        const fetchAllData = async () => {
            try {
                const [calls, puts, newPos, increased, reduced, funds, closed] = await Promise.all([
                    StockService.getTopByCallOptions(topN),
                    StockService.getTopByPutOptions(topN),
                    StockService.getTopByNewPositions(topN),
                    StockService.getTopByIncreasedPositions(topN),
                    StockService.getTopByReducedPositions(topN),
                    StockService.getTopByFundsHolding(topN),
                    StockService.getTopByClosedPositions(topN),
                ]);

                setData({
                    calls: mapCalls(getArr(calls)),
                    puts: mapPuts(getArr(puts)),
                    newPositions: mapNewPos(getArr(newPos)),
                    increased: mapIncreased(getArr(increased)),
                    reduced: mapReduced(getArr(reduced)),
                    fundsHolding: mapFunds(getArr(funds)),
                    closed: mapClosed(getArr(closed)),
                });

                setLoadingStates({
                    calls: false,
                    puts: false,
                    newPositions: false,
                    increased: false,
                    reduced: false,
                    fundsHolding: false,
                    closed: false,
                });
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                // Set all loading states to false on error
                setLoadingStates({
                    calls: false,
                    puts: false,
                    newPositions: false,
                    increased: false,
                    reduced: false,
                    fundsHolding: false,
                    closed: false,
                });
            }
        };

        fetchAllData();
    }, [topN]);

    const textPrimary = theme === 'dark' ? 'text-theme-primary' : 'text-gray-900';
    const textSecondary = theme === 'dark' ? 'text-theme-muted' : 'text-gray-600';

    return (
        <div className="space-y-6 sm:space-y-8 animate-fadeIn w-full max-w-full overflow-hidden">
            {/* Header with Title and Top N Filter on same line */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 px-2 sm:px-4">
                <h2 className={`text-lg sm:text-xl font-medium ${textPrimary}`}>Institutional Market Pulse</h2>

                {/* Top N Filter */}
                <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold ${textSecondary} whitespace-nowrap`}>Show Top:</span>
                    <div className="flex items-center gap-2">
                        {[5, 10, 15, 20].map(n => (
                            <button
                                key={n}
                                onClick={() => setTopN(n)}
                                className={`min-w-[60px] px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 touch-manipulation ${topN === n
                                    ? 'bg-theme-primary text-white shadow-md scale-105'
                                    : `${theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
                                    }`}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Explanatory Info Banner - Mobile Optimized */}
            {/* <div className={`mb-4 sm:mb-6 p-4 sm:p-5 rounded-xl border ${theme === 'dark' ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start gap-3">
                    <Info className={`w-5 h-5 mt-0.5 sm:mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    <div className={`${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'} leading-relaxed`}>
                        <p className="font-semibold mb-1.5 text-sm sm:text-base">Understanding the Metrics</p>
                        <p className="text-sm sm:text-base">Each metric is calculated by summing data from the <strong>top {topN} stocks</strong> in each category. Tap the <Info className="w-3.5 h-3.5 inline mx-0.5" /> icons for details.</p>
                    </div>
                </div>
            </div> */}

            {/* Sentiment / Highlights Section - Mobile Optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
                {/* Bullish Sentiment Card */}
                <div className={`p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 flex items-center justify-between group relative overflow-hidden`}>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-green-600 dark:text-green-400 font-semibold text-sm sm:text-base truncate">Bullish Sentiment</div>
                            <div className="relative group/tooltip">
                                <Info className="w-4 h-4 text-green-600 dark:text-green-400 opacity-60 hover:opacity-100 cursor-help transition-opacity" />
                                <div className={`absolute left-0 top-6 w-72 p-3 rounded-lg shadow-xl border opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                                    <div className="text-xs space-y-1.5">
                                        <p className="font-semibold">Total value of call options held</p>
                                        <p>Aggregated from the top 5 stocks with highest call option activity. Represents bullish bets by institutions.</p>
                                        <p className="text-green-600 dark:text-green-400 font-medium">When this exceeds Bearish/Hedging, it signals net institutional optimism in these stocks</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`text-xl sm:text-2xl font-bold ${textPrimary}`}>
                            {loadingStates.calls ? '...' : formatNumber(totals.calls)}
                        </div>
                        <div className={`text-xs sm:text-sm ${textSecondary} mt-2`}>Total Call Options (Top {topN})</div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                </div>

                {/* High Conviction Card */}
                <div className={`p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/5 border border-blue-500/20 flex items-center justify-between group relative overflow-hidden`}>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-blue-600 dark:text-blue-400 font-semibold text-sm sm:text-base truncate">High Conviction</div>
                            <div className="relative group/tooltip">
                                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 opacity-60 hover:opacity-100 cursor-help transition-opacity" />
                                <div className={`absolute left-0 top-6 w-72 p-3 rounded-lg shadow-xl border opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                                    <div className="text-xs space-y-1.5">
                                        <p className="font-semibold">Number of new positions opened</p>
                                        <p>Summed from the top 5 stocks with most new institutional entries. Shows where smart money is making fresh bets.</p>
                                        <p className="text-blue-600 dark:text-blue-400 font-medium">Thousands of new positions indicate concentrated institutional interest in these stocks</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`text-xl sm:text-2xl font-bold ${textPrimary}`}>
                            {loadingStates.newPositions ? '...' : totals.newPositions.toLocaleString()}
                        </div>
                        <div className={`text-xs sm:text-sm ${textSecondary} mt-2`}>Total New Positions (Top {topN})</div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
                        <Activity className="w-6 h-6" />
                    </div>
                </div>

                {/* Bearish/Hedging Card */}
                <div className={`p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-red-500/10 to-orange-500/5 border border-red-500/20 flex items-center justify-between group relative overflow-hidden`}>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-red-600 dark:text-red-400 font-semibold text-sm sm:text-base truncate">Bearish / Hedging</div>
                            <div className="relative group/tooltip">
                                <Info className="w-4 h-4 text-red-600 dark:text-red-400 opacity-60 hover:opacity-100 cursor-help transition-opacity" />
                                <div className={`absolute left-0 top-6 w-72 p-3 rounded-lg shadow-xl border opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-50 ${theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                                    <div className="text-xs space-y-1.5">
                                        <p className="font-semibold">Total value of put options held</p>
                                        <p>Aggregated from the top 5 stocks with highest put option activity. May indicate bearish bets or portfolio hedging.</p>
                                        <p className="text-red-600 dark:text-red-400 font-medium">When this exceeds Bullish Sentiment, it signals institutional caution or active downside protection</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`text-xl sm:text-2xl font-bold ${textPrimary}`}>
                            {loadingStates.puts ? '...' : formatNumber(totals.puts)}
                        </div>
                        <div className={`text-xs sm:text-sm ${textSecondary} mt-2`}>Total Put Options (Top {topN})</div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center text-red-600 dark:text-red-400 flex-shrink-0">
                        <TrendingDown className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {/* Main Grid - Mobile Optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                <DashboardCard
                    title="Most Held Stocks"
                    icon={<Briefcase />}
                    data={data.fundsHolding}
                    loading={loadingStates.fundsHolding}
                    type="count"
                    color="text-purple-500 bg-purple-500"
                    topN={topN}
                />
                <DashboardCard
                    title="New Positions"
                    icon={<Layers />}
                    data={data.newPositions}
                    loading={loadingStates.newPositions}
                    type="count"
                    color="text-blue-500 bg-blue-500"
                    topN={topN}
                />
                <DashboardCard
                    title="Increased Stakes"
                    icon={<ArrowUp />}
                    data={data.increased}
                    loading={loadingStates.increased}
                    type="count"
                    color="text-green-500 bg-green-500"
                    topN={topN}
                />
                <DashboardCard
                    title="Reduced Stakes"
                    icon={<ArrowDown />}
                    data={data.reduced}
                    loading={loadingStates.reduced}
                    type="count"
                    color="text-orange-500 bg-orange-500"
                    topN={topN}
                />
                <DashboardCard
                    title="Sold Out (Closed)"
                    icon={<PieChart />}
                    data={data.closed}
                    loading={loadingStates.closed}
                    type="count"
                    color="text-gray-500 bg-gray-500"
                    topN={topN}
                />
                <DashboardCard
                    title="Top Call Options"
                    icon={<TrendingUp />}
                    data={data.calls}
                    loading={loadingStates.calls}
                    type="number"
                    color="text-emerald-500 bg-emerald-500"
                    topN={topN}
                />
                <DashboardCard
                    title="Top Put Options"
                    icon={<TrendingDown />}
                    data={data.puts}
                    loading={loadingStates.puts}
                    type="number"
                    color="text-red-500 bg-red-500"
                    topN={topN}
                />
            </div>
        </div >
    );
};

export default InstitutionalActivityDashboard;
